// RootEleve.jsx
import React, { useEffect, useState, useCallback } from "react";
import EspaceEleve from "./EspaceEleve/EspaceEleve";
import { useClasseContext } from "../context/ClasseContext";
import { useUser } from "../context/UserContext";
import { XpProvider } from "../context/XpContext";
import { url } from "../utils/confserveur";
import Loading from "../components/general/Loading";
import TanoCharacter from "../components/Eleve/TanoCharacter";

/**
 * Racine de l'espace élève — route sur le get-sync-status NOUVEAU format
 * (fiche unique, spec GAR §3.3) :
 *   non-GAR : { is_gar: false, eleve_classique: {...} }
 *   GAR     : { is_gar: true, statut: 'lie'|'inactif'|'attente',
 *               gar_info: {...}, eleve: {...}|null }
 * La détection GAR vient de l'API (is_gar) — plus de copie localStorage.
 */
function RootEleve() {
    const { user, syncEleve, setSyncEleve } = useUser();
    const { setEtablissement, setClasseNom } = useClasseContext();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchEleveData = useCallback(async () => {
        if (!user?.id_user) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `${url}eleves/get-sync-status/?user_id=${user.id_user}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Erreur lors de la récupération des données élève');
            }

            const data = await response.json();
            setSyncEleve(data);

        } catch (err) {
            console.error('Erreur:', err);
            setError('Erreur lors de la récupération des données. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [user?.id_user, setSyncEleve]);

    // Un seul appel API au montage
    useEffect(() => {
        fetchEleveData();
    }, [fetchEleveData]);

    // Mettre à jour les données d'établissement selon le type d'élève
    useEffect(() => {
        if (!syncEleve) return;

        // Fiche unique : une seule source, classique ou GAR lié
        const eleveData = syncEleve.is_gar === false
            ? syncEleve.eleve_classique
            : syncEleve.eleve;

        if (eleveData) {
            if (eleveData.etablissement) {
                setEtablissement(eleveData.etablissement);
                localStorage.setItem('etablissement', eleveData.etablissement);
            }
            if (eleveData.classe_nom) {
                setClasseNom(eleveData.classe_nom);
                localStorage.setItem('classeNom', eleveData.classe_nom);
            }
        }
    }, [syncEleve, setEtablissement, setClasseNom]);

    // Affichage du chargement
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
               <Loading loadingText={"Chargement des données.."} />
            </div>
        );
    }

    if (!syncEleve) return null;

    // Cas 1: Élève classique (non GAR)
    if (syncEleve.is_gar === false) {
        const eleveData = syncEleve.eleve_classique;

        if (eleveData && eleveData.eleve_data) {
            return <XpProvider><EspaceEleve eleveBDD={eleveData.eleve_data} isGarEleve={false} /></XpProvider>;
        }
    }

    // Cas 2: Élève GAR
    if (syncEleve.is_gar === true) {
        // Fiche liée et active → espace élève (une seule fiche pour tout)
        if (syncEleve.statut === 'lie' && syncEleve.eleve?.eleve_data) {
            return (
                <XpProvider>
                    <EspaceEleve eleveBDD={syncEleve.eleve.eleve_data} isGarEleve={true} />
                </XpProvider>
            );
        }

        // Pas (encore) de fiche liée → ÉCRAN D'ATTENTE (spec §2/§10).
        // Clair et sympa (c'est un enfant), et NEUTRE : un élève hors licence le
        // verra pour toujours — aucune promesse de résolution, un texte qui
        // reste vrai dans tous les cas. C'est le filet, pas le parcours normal
        // (l'auto-liaison et le balayage traitent l'évident côté serveur).
        const prenom = syncEleve.gar_info?.prenom || '';
        return (
            <div
                className="min-h-screen flex items-center justify-center p-6"
                style={{ background: 'linear-gradient(180deg,#0A1F3D,#122E54 62%,#0A1F3D)' }}
            >
                <div className="relative max-w-md w-full">
                    <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-3xl p-8 text-center shadow-2xl">
                        <div className="flex justify-center mb-4">
                            <TanoCharacter mood="idle" size={110} />
                        </div>

                        <h2 className="text-2xl font-bold text-white mb-2">
                            {prenom ? `Salut ${prenom} !` : 'Salut !'}
                        </h2>
                        <p className="text-cyan-200/90 font-medium text-sm mb-4">
                            Ton compte fonctionne ✨
                        </p>
                        <p className="text-slate-200 text-sm mb-6 leading-relaxed">
                            Il n'est pas encore rattaché à ta classe dans TanoEd.
                            Si tu viens d'arriver, demande à ton professeur de maths ou de français
                            de t'ajouter à sa classe. Ton espace s'ouvrira alors tout seul.
                            Reviens vérifier de temps en temps !
                        </p>

                        <button
                            onClick={fetchEleveData}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold text-sm shadow-lg shadow-cyan-500/30 transition-all hover:-translate-y-0.5"
                        >
                            🔄 Revérifier
                        </button>

                        {error ? (
                            <p className="text-xs text-orange-300 mt-4">{error}</p>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }

    // Cas par défaut
    return null;
}

export default RootEleve;
